import BorderlessSelect from '../../../components/BorderlessSelect';
import WorkspaceDialog from '../../../components/WorkspaceDialog';
import CanonicalScoreField from '../../../components/CanonicalScoreField';
import TennisScoreboard from '../../../components/TennisScoreboard';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ListChecks,
  Loader2,
  RefreshCw,
  Trophy,
} from 'lucide-react';
import { Button, Input } from '@tark/shared/components';
import {
  useCompleteOrganizerCompetitionDivision,
  useOrganizerCompetitionResults,
  useResolveOrganizerCompetitionResult,
} from '@tark/shared/hooks';
import {
  getErrorMessage,
  type CompetitionResultDivision,
  type CompetitionResultMatch,
} from '@tark/shared/lib';

type ViewMode = 'results' | 'leaderboard';
type ResultOutcome = 'COMPLETED' | 'RETIREMENT' | 'WALKOVER' | 'NO_SHOW' | 'ABANDONED' | 'VOID';

export default function OrganizerResultsPanel({
  competitionPublicId,
  published,
  canAdmin,
  displayMode,
}: {
  competitionPublicId: string;
  published: boolean;
  canAdmin: boolean;
  displayMode?: ViewMode;
}) {
  const query = useOrganizerCompetitionResults(competitionPublicId, published);
  const resolve = useResolveOrganizerCompetitionResult(competitionPublicId);
  const complete = useCompleteOrganizerCompetitionDivision(competitionPublicId);
  const [localMode, setMode] = useState<ViewMode>('results');
  const mode = displayMode ?? localMode;
  const [divisionId, setDivisionId] = useState('');
  const [resolving, setResolving] = useState<CompetitionResultMatch | null>(null);
  const [outcome, setOutcome] = useState<ResultOutcome>('COMPLETED');
  const [winnerSide, setWinnerSide] = useState<'A' | 'B' | 'NONE'>('A');
  const [scoreText, setScoreText] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const divisions = query.data?.divisions ?? [];

  useEffect(() => {
    if (!divisions.some((division) => division.divisionPublicId === divisionId)) {
      setDivisionId(divisions[0]?.divisionPublicId ?? '');
    }
  }, [divisionId, divisions]);

  const division = useMemo(
    () => divisions.find((item) => item.divisionPublicId === divisionId) ?? divisions[0],
    [divisionId, divisions],
  );
  const matches = useMemo(
    () => (query.data?.matches ?? []).filter((match) => match.divisionPublicId === division?.divisionPublicId),
    [division?.divisionPublicId, query.data?.matches],
  );

  if (!published) return null;

  const openResolve = (match: CompetitionResultMatch) => {
    setResolving(match);
    setOutcome(match.currentResult?.outcomeCode as ResultOutcome || 'COMPLETED');
    setWinnerSide((match.currentResult?.winnerSide as 'A' | 'B' | 'NONE') || 'A');
    setScoreText(match.currentResult?.canonicalScore || '');
    setDurationMinutes(match.currentResult?.durationMinutes?.toString() || '');
    setReason('');
    setMessage(null);
  };

  const submitResolution = async () => {
    if (!resolving || reason.trim().length < 3) return;
    const timed = needsDuration(resolving, outcome);
    const parsedDuration = Number(durationMinutes);
    if (timed && (!Number.isInteger(parsedDuration) || parsedDuration < 1 || parsedDuration > 480)) {
      setMessage({ tone: 'error', text: 'Enter minutes played between 1 and 480.' });
      return;
    }
    setMessage(null);
    try {
      await resolve.mutateAsync({
        matchPublicId: resolving.matchPublicId,
        request: {
          clientMutationId: createMutationId(),
          expectedMatchVersion: resolving.version,
          outcomeCode: outcome,
          scoreText: needsScore(outcome) ? scoreText.trim() : null,
          durationMinutes: timed ? parsedDuration : null,
          winnerSide: allowsWinner(outcome) ? winnerSide : 'NONE',
          reason: reason.trim(),
        },
      });
      setResolving(null);
      setMessage({ tone: 'success', text: 'Result resolved and the leaderboard recalculated.' });
    } catch (error) {
      setMessage({ tone: 'error', text: resultError(error) });
    }
  };

  const completeDivision = async (selected: CompetitionResultDivision) => {
    setMessage(null);
    try {
      await complete.mutateAsync({
        divisionPublicId: selected.divisionPublicId,
        reason: 'All scheduled results reviewed and division finalized',
      });
      setMessage({ tone: 'success', text: `${selected.name} finalized.` });
    } catch (error) {
      setMessage({ tone: 'error', text: resultError(error) });
    }
  };

  return (
    <section className="border-b border-border py-5" aria-labelledby="competition-results-title">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Trophy className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h2 id="competition-results-title" className="text-lg font-semibold text-text">{mode === 'leaderboard' ? 'Standings' : 'Results'}</h2>

          </div>
        </div>
        <Button size="sm" variant="ghost" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={() => void query.refetch()}>
          Refresh
        </Button>
      </div>

      {query.isLoading ? (
        <div className="flex min-h-28 items-center justify-center gap-2 text-sm text-text-muted">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading results
        </div>
      ) : query.isError ? (
        <div className="mt-4 flex items-center justify-between gap-3 border-l-2 border-red-500 pl-3">
          <p className="text-sm text-text-muted">Results could not be loaded.</p>
          <Button size="sm" variant="outline" onClick={() => void query.refetch()}>Retry</Button>
        </div>
      ) : division ? (
        <div className="mt-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div hidden={Boolean(displayMode)} className="inline-flex rounded-lg border border-border bg-surface-elevated p-1" role="tablist" aria-label="Competition results views">
              <ViewButton active={mode === 'results'} icon={<ListChecks className="h-4 w-4" />} label="Results" onClick={() => setMode('results')} />
              <ViewButton active={mode === 'leaderboard'} icon={<Trophy className="h-4 w-4" />} label="Leaderboard" onClick={() => setMode('leaderboard')} />
            </div>
            <BorderlessSelect
              aria-label="Results division"
              className="tk-filter"
              value={division.divisionPublicId}
              onChange={(event) => setDivisionId(event.target.value)}
            >
              {divisions.map((item) => <option key={item.divisionPublicId} value={item.divisionPublicId}>{item.name}</option>)}
            </BorderlessSelect>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-y border-border py-3">
            <div>
              <p className="font-semibold text-text">{division.name}</p>
              <p className="mt-0.5 text-xs font-semibold text-text-muted">
                {division.leaderboardState === 'FINAL' ? 'Final standings' : `${division.completionBlockerCount} result${division.completionBlockerCount === 1 ? '' : 's'} need attention`}
              </p>
            </div>
            {canAdmin && division.leaderboardState !== 'FINAL' && division.completionBlockerCount === 0 ? (
              <Button size="sm" leftIcon={<CheckCircle2 className="h-4 w-4" />} disabled={complete.isPending} onClick={() => void completeDivision(division)}>
                Finalize division
              </Button>
            ) : null}
          </div>

          {message ? <InlineMessage tone={message.tone} text={message.text} /> : null}

          {mode === 'results' ? (
            <ResultRows matches={matches} canAdmin={canAdmin} onResolve={openResolve} />
          ) : (
            <Leaderboard division={division} />
          )}
        </div>
      ) : (
        <p className="mt-5 text-sm text-text-muted">Results appear after the published draw creates matches.</p>
      )}

      {resolving ? (
        <WorkspaceDialog title="Resolve result" onClose={() => setResolving(null)} busy={resolve.isPending}>
            <p className="mt-1 text-sm text-text-muted">{names(resolving.sideA.displayNames)} vs {names(resolving.sideB.displayNames)}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Outcome">
                <select className="h-11 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text" value={outcome} onChange={(event) => setOutcome(event.target.value as ResultOutcome)}>
                  <option value="COMPLETED">Completed</option>
                  <option value="RETIREMENT">Retirement</option>
                  <option value="WALKOVER">Walkover</option>
                  <option value="NO_SHOW">No show</option>
                  <option value="ABANDONED">Abandoned</option>
                  <option value="VOID">Void</option>
                </select>
              </Field>
              <Field label="Winner">
                <select className="h-11 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text" value={allowsWinner(outcome) ? winnerSide : 'NONE'} disabled={!allowsWinner(outcome)} onChange={(event) => setWinnerSide(event.target.value as 'A' | 'B' | 'NONE')}>
                  <option value="A">{names(resolving.sideA.displayNames)}</option>
                  <option value="B">{names(resolving.sideB.displayNames)}</option>
                  <option value="NONE">No winner</option>
                </select>
              </Field>
            </div>
            {needsScore(outcome) ? (
              <div className="mt-4"><CanonicalScoreField key={resolving.matchPublicId} value={scoreText} onChange={setScoreText} rawOnly={resolving.scoringFormatCode === 'TIMED_GAMES'} /></div>
            ) : null}
            {needsDuration(resolving, outcome) ? (
              <div className="mt-4"><Input label="Minutes played" type="number" min={1} max={480} value={durationMinutes} onChange={(event) => setDurationMinutes(event.target.value)} placeholder="60" /></div>
            ) : null}
            <div className="mt-4"><Input label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="What was verified?" /></div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" disabled={resolve.isPending} onClick={() => setResolving(null)}>Cancel</Button>
              <Button disabled={resolve.isPending || reason.trim().length < 3} onClick={() => void submitResolution()}>
                {resolve.isPending ? 'Saving...' : 'Save resolution'}
              </Button>
            </div>
        </WorkspaceDialog>
      ) : null}
    </section>
  );
}

function ResultRows({ matches, canAdmin, onResolve }: { matches: CompetitionResultMatch[]; canAdmin: boolean; onResolve: (match: CompetitionResultMatch) => void }) {
  if (!matches.length) return <p className="py-6 text-sm text-text-muted">No matches in this division yet.</p>;
  return <div className="grid gap-3 lg:grid-cols-2">{matches.map(match => (
    <div key={match.matchPublicId} className="space-y-2">
      <TennisScoreboard sideA={match.sideA.displayNames} sideB={match.sideB.displayNames}
        score={match.currentResult?.canonicalScore} emptyLabel={match.currentResult ? formatState(match.currentResult.outcomeCode) : 'Not reported'} winner={match.currentResult?.winnerSide}
        label={`Round ${match.roundNumber} · Match ${match.matchNumber}`} status={<StatePill state={match.resultState} />} />
      <div className="flex justify-end gap-2">
        {canAdmin && <Button size="sm" variant="ghost" onClick={() => onResolve(match)}>{['FINALIZED', 'VOIDED'].includes(match.resultState) ? 'Correct' : 'Resolve'}</Button>}
      </div>
    </div>
  ))}</div>;
}

function Leaderboard({ division }: { division: CompetitionResultDivision }) {
  if (!division.standings.length) return <p className="py-6 text-sm text-text-muted">Standings appear after the first confirmed result.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[620px] text-left text-sm">
        <thead className="border-b border-border text-xs font-semibold text-text-muted"><tr><th className="py-3 pr-3">Rank</th><th className="py-3 pr-3">Player / team</th><th className="py-3 text-center">P</th><th className="py-3 text-center">W</th><th className="py-3 text-center">L</th><th className="py-3 text-center">Sets</th><th className="py-3 text-center">Games</th><th className="py-3 text-right">Pts</th></tr></thead>
        <tbody className="divide-y divide-border">{division.standings.map((row) => (
          <tr key={row.entryPublicId} className={row.mine ? 'bg-primary/5' : ''}>
            <td className="py-3 pr-3 font-semibold text-text">{row.rank}</td><td className="py-3 pr-3 font-semibold text-text">{names(row.displayNames)}{row.mine ? <span className="ml-2 text-xs text-primary">You</span> : null}</td><td className="py-3 text-center text-text-muted">{row.played}</td><td className="py-3 text-center text-text-muted">{row.wins}</td><td className="py-3 text-center text-text-muted">{row.losses}</td><td className="py-3 text-center text-text-muted">{row.setsWon}-{row.setsLost}</td><td className="py-3 text-center text-text-muted">{row.gamesWon}-{row.gamesLost}</td><td className="py-3 text-right font-bold text-text">{row.points}</td>
          </tr>
        ))}</tbody>
      </table>
      <p className="mt-3 text-xs text-text-muted">{division.leaderboardState === 'FINAL' ? 'Final' : 'Provisional'} · Exact ties share a rank.</p>
    </div>
  );
}

function ViewButton({ active, icon, label, onClick }: { active: boolean; icon: ReactNode; label: string; onClick: () => void }) {
  return <button type="button" role="tab" aria-selected={active} className={`flex h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold ${active ? 'bg-surface text-primary shadow-sm' : 'text-text-muted hover:text-text'}`} onClick={onClick}>{icon}{label}</button>;
}

function StatePill({ state }: { state: string }) {
  const good = state === 'FINALIZED';
  const warning = ['DISPUTED', 'AWAITING_RESULT', 'AWAITING_CONFIRMATION'].includes(state);
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${good ? 'bg-emerald-500/10 text-emerald-700' : warning ? 'bg-amber-500/10 text-amber-700' : 'bg-surface-elevated text-text-muted'}`}>{formatState(state)}</span>;
}

function InlineMessage({ tone, text }: { tone: 'success' | 'error'; text: string }) {
  const Icon = tone === 'success' ? CheckCircle2 : AlertCircle;
  return <div role={tone === 'error' ? 'alert' : 'status'} className={`flex items-center gap-2 text-sm font-medium ${tone === 'error' ? 'text-red-600' : 'text-emerald-700'}`}><Icon className="h-4 w-4" />{text}</div>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="space-y-1"><span className="text-sm font-semibold text-text">{label}</span>{children}</label>;
}

function names(value: string[]): string { return value.filter(Boolean).join(' / ') || 'Entry unavailable'; }
function formatState(value: string): string { return value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (letter) => letter.toUpperCase()); }
function needsScore(outcome: ResultOutcome): boolean { return ['COMPLETED', 'RETIREMENT'].includes(outcome); }
function needsDuration(match: CompetitionResultMatch, outcome: ResultOutcome): boolean {
  return match.scoringFormatCode === 'TIMED_GAMES' && ['COMPLETED', 'RETIREMENT', 'ABANDONED'].includes(outcome);
}
function allowsWinner(outcome: ResultOutcome): boolean { return !['ABANDONED', 'VOID'].includes(outcome); }
function resultError(error: unknown): string {
  const message = getErrorMessage(error).toLowerCase();
  if (message.includes('stale') || message.includes('changed') || message.includes('version')) return 'This result changed. Refresh and review the latest version.';
  if (message.includes('score')) return 'Check the score format and try again.';
  if (message.includes('incomplete') || message.includes('attention')) return 'Resolve every outstanding match before finalizing.';
  return 'The result could not be saved. Refresh and try again.';
}
function createMutationId(): string { return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`; }
